'use client'

import React from 'react'

import { ProductCard } from '@src/components/product-card/product-card.component'
import { getInitialSelectedVariant, navigateToPDP } from '@src/components/product-card/product-card.helpers'
import { SvdProductCard } from '@src/components/svd-product-card'
import { isPlSvdCard, mapPlSvdCard, type PlSvdCard } from '@src/components/svd-product-card/map-pl-svd-card'
import { getSvdCardListKey } from '@src/components/svd-product-card/svd-product-card.helpers'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { trackingMetaData } from '@src/pages/[...slug]/components/collection/collection.constants'
import CollectionFilters from '@src/pages/[...slug]/components/collection/molecules/collectionFilters/CollectionFilters'
import NoProduct from '@src/pages/[...slug]/components/collection/molecules/noProducts'
import { LoaderWrapper, Skeleton } from '@src/quince-components/src'
import Product from '@src/quince-site-common/src/domains/Product'
import { useIsSvdEnabled } from '@src/quince-site-common/src/hooks/use-is-svd-enabled'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { MAX_NUMBER_OF_COLOR_OPTIONS } from '@src/quince-site-common/src/utils/constants'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import { TPDPSectionData } from '@src/quince-site-pdp/src/types/pdp-section-data.type'

import { MINI_PDP_COLLECTION_SECTION_NAME_FOR_REF } from './constant'
import styles from './miniPDPCollection.module.scss'
import { useMiniPDPCollection } from './useMiniPDPCollection'

const TRACKING_CATEGORY_NAVIGATION = 'navigation'

export function MiniPDPCollection({
  collectionId,
  product,
  sectionData,
}: Readonly<{
  collectionId: string
  product: Product
  sectionData: TPDPSectionData
}>) {
  const {
    collectionPage,
    totalProductsCount,
    initialFilters,
    sortBy,
    selectedFilters,
    updateQuery,
    showSubCollectionsPageLoader,
    showNextFeedLoading,
    filteredSubCollection,
    totalProductsCurrentlyOnPage,
    intersectRef,
    productViewTracker,
    filterSectionRef,
    productSectionRef,
    productCardsSectionRef,
    tracker,
    router,
  } = useMiniPDPCollection({
    collectionId,
    product,
    sectionData,
  })

  const { deviceType } = useDeviceInfo()
  const isDesktop = deviceType === DEVICE_TYPES.DESKTOP
  // TEMPORARY (STRFRNT-15810 · self-fetcher SVD preview): render the native SVD card once the
  // collection service backing `useMiniPDPCollection` migrates to the SVD wire shape. `isPlSvdCard`
  // shape-guards on anchor data, so today's legacy-shaped response always falls through to the
  // legacy `ProductCard` below — inert until that fetch migrates.
  const isSvdEnabled = useIsSvdEnabled()

  const maxWidth = getMaxWidthInPx({
    maxWidthMobileInRem: styles.productCardWidthMobile ?? '',
    maxWidthDesktopInRem: styles.productCardWidth ?? '',
    deviceType: deviceType,
  })

  return (
    <div className={styles.miniPDPCollectionWrapper} data-section-name={MINI_PDP_COLLECTION_SECTION_NAME_FOR_REF}>
      <div id="filter-section" ref={filterSectionRef}>
        <CollectionFilters
          totalProductsCount={totalProductsCount}
          initialFilters={initialFilters}
          sortBy={sortBy}
          selectedFilters={selectedFilters}
          updateQuery={updateQuery}
          collectionPage={collectionPage}
          showLoader={showSubCollectionsPageLoader && initialFilters.length === 0}
          subCollectionCards={[]}
          filtersContainerClassName={styles.filtersWrapper}
        />
      </div>
      <div ref={productSectionRef}>
        <div className={styles.subcollection_wrapper}>
          {showSubCollectionsPageLoader ? (
            <div className={styles.productCardsSkeletonWrapper}>
              {new Array(isDesktop ? 8 : 4).fill(0).map((_, index) => (
                <Skeleton key={index} className={styles.productCardSkeleton} />
              ))}
            </div>
          ) : (
            <>
              <div className={styles.productCardsWrapper}>
                {filteredSubCollection?.productCards?.map((productCard, idx) => {
                  if (isSvdEnabled && isPlSvdCard(productCard.product)) {
                    const svdProduct = mapPlSvdCard(productCard.product as unknown as PlSvdCard)
                    return (
                      // Inert branch: `useMiniPDPCollection` fetches via `collectionService`
                      // `getCollectionById` (v3, no SVD path), so `isPlSvdCard` never matches and the
                      // dropped `showWhiteGloveDeliveryTag` cannot diverge. Surface is owned by the
                      // PDP/SVD-buy-box track.
                      <SvdProductCard
                        key={getSvdCardListKey(svdProduct)}
                        product={svdProduct}
                        className={styles.productCard}
                        optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
                        productViewTracker={productViewTracker}
                        productClickTrackConfig={{
                          trackInternal: true,
                          trackGA: true,
                          category: TRACKING_CATEGORY_NAVIGATION,
                          action: 'click_subcollection_product',
                          context: {
                            product_position: idx,
                            ...trackingMetaData.context,
                          },
                          value: svdProduct.slug,
                        }}
                        onShowMoreClick={({ selectedVariant }) => {
                          navigateToPDP({
                            slug: svdProduct.slug,
                            tracker: tracker && `${tracker}__${idx}`,
                            selectedOption: selectedVariant?.displayConfig?.value,
                            router,
                          })
                        }}
                        maxWidth={maxWidth}
                        eagerLoadImage={isDesktop ? idx < 4 : idx < 2}
                        tracker={tracker && `${tracker}__${idx}`}
                      />
                    )
                  }

                  const initialSelectedVariant = getInitialSelectedVariant({
                    cardVariants: productCard.product.cardVariants,
                  })
                  return (
                    <ProductCard
                      key={`${productCard.product.productId}_${initialSelectedVariant?.displayConfig.value}`}
                      productCard={productCard}
                      className={styles.productCard}
                      optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
                      shouldShowMore
                      productViewTracker={productViewTracker}
                      productClickTrackConfig={{
                        trackInternal: true,
                        trackGA: true,
                        category: TRACKING_CATEGORY_NAVIGATION,
                        action: 'click_subcollection_product',
                        context: {
                          product_id: productCard.product.relatedIds.spid,
                          product_color: initialSelectedVariant?.displayConfig.value,
                          product_position: idx,
                          ...trackingMetaData.context,
                        },
                        value: productCard.product.slug,
                      }}
                      onShowMoreClick={({ selectedVariant }) => {
                        navigateToPDP({
                          slug: productCard.product.slug,
                          tracker: tracker && `${tracker}__${idx}`,
                          selectedOption: selectedVariant?.displayConfig.value,
                          router,
                        })
                      }}
                      maxWidth={maxWidth}
                      eagerLoadImage={isDesktop ? idx < 4 : idx < 2}
                      tracker={tracker && `${tracker}__${idx}`}
                      showWhiteGloveDeliveryTag
                    />
                  )
                })}
              </div>

              <div id="boundary_bottom" className={styles.boundary_bottom} ref={intersectRef} />
              <LoaderWrapper
                testid={TEST_LOCATORS.COLLECTION_INFINITE_LOADER}
                showLoader={showNextFeedLoading}
                // eslint-disable-next-line i18next/no-literal-string
                minHeight={'40rem'}
              >
                <div />
              </LoaderWrapper>
            </>
          )}
        </div>
      </div>
      {totalProductsCurrentlyOnPage === 0 && !showSubCollectionsPageLoader && (
        <NoProduct
          onResetFilters={() => {
            updateQuery({
              filter: { type: 'reset' },
              sort: { type: 'reset' },
            })
          }}
          ref={productCardsSectionRef}
        />
      )}
    </div>
  )
}
